import React, { useMemo, useLayoutEffect } from 'react';
import { Provider } from 'react-redux';
import { Router, Route } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { HelmetProvider } from 'react-helmet-async';
import { renderRoutes } from 'react-router-config';
import { Batch } from '@elevio-kb/eventer';
import './i18n';
import { Config, Context as ConfigContext } from './Config';
import { Provider as ScriptProvider } from './toolkit/InnerHtml';
import { getInitialData } from './utils/globals';
import { setupStore } from './reduxStore';
import { setupAnalytics, setAnalyticsIsEnabled } from './analytics';
import { setupBugTracking } from './bugTracking';
import { setup as setupClientApi } from './clientApi';
import { createHistory } from './createHistory';
import { LoadingBar } from './toolkit/loading';
import AppRoutes, { getRoutes } from './Routes';

export let batch: Batch;

ssoRedirectCheck();
setupBugTracking();
if (process.env.NODE_ENV !== 'test') {
  const ana = setupAnalytics();
  batch = ana.batch;
  setupClientApi();
} else {
  batch = new Batch({ handler() {} });
}

const store = setupStore(getInitialData(), window.location.pathname);

type Props = {
  config: Config;
};
const App: React.FC<Props> = ({ config }) => {
  const history = useMemo(() => createHistory(config), [config]);

  useLayoutEffect(() => {
    setAnalyticsIsEnabled(true);
  }, []);

  const routes = getRoutes(config);

  return (
    <React.StrictMode>
      <ConfigContext.Provider value={config}>
        <HelmetProvider>
          <Provider store={store}>
            {config.hideLoadingProgress ? null : <LoadingBar />}
            <Router history={history}>
              <QueryParamProvider ReactRouterRoute={Route}>
                <ScriptProvider>
                  <AppRoutes routes={routes}>{renderRoutes(routes)}</AppRoutes>
                </ScriptProvider>
              </QueryParamProvider>
            </Router>
          </Provider>
        </HelmetProvider>
      </ConfigContext.Provider>
    </React.StrictMode>
  );
};

export default App;

// Check for OIDC format SSO token
function ssoRedirectCheck() {
  if (
    window.location.href.includes('#') &&
    window.location.href.includes('id_token')
  ) {
    window.location.href = `${window.location.protocol}//${
      window.location.hostname
    }/sso/check?${window.location.href
      .split('#')[1]
      .replace('id_token', 'jwt')}`;
  }
}
